import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import sanityClient from '../../lib/sanityClient';
import styles from './Blog.module.scss';

const isNewPost = (postDate, days = 7) => {
  const now = new Date();
  const post = new Date(postDate);
  const diffTime = now - post;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  return diffDays < days;
};

// 🔢 Limity
const SIDEBAR_TAG_LIMIT = 6;
const CARD_TAG_LIMIT = 5;

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);

  // 🔹 czy w sidebarze pokazujemy wszystkie tagi
  const [showAllSidebarTags, setShowAllSidebarTags] = useState(false);

  // 🔹 które posty mają rozwinięte tagi (mapa: postId -> true/false)
  const [expandedTagsPosts, setExpandedTagsPosts] = useState({});

  useEffect(() => {
    sanityClient
      .fetch(`*[_type == "post"] | order(date desc) {
        _id,
        title,
        slug,
        excerpt,
        date,
        tags,
        "image": image.asset->url
      }`)
      .then(setBlogPosts)
      .catch(console.error);
  }, []);

  const tagCounts = blogPosts.reduce((acc, post) => {
    post.tags?.forEach(tag => {
      acc[tag] = (acc[tag] || 0) + 1;
    });
    return acc;
  }, {});

  const allTags = Object.keys(tagCounts);

  const filteredPosts = selectedTag
    ? blogPosts.filter(post => post.tags?.includes(selectedTag))
    : blogPosts;

  // 🔹 tagi widoczne w sidebarze (limit + "pokaż więcej")
  const visibleSidebarTags = showAllSidebarTags
    ? allTags
    : allTags.slice(0, SIDEBAR_TAG_LIMIT);

  return (
    <section className={styles.blog}>
      <div
        className={styles.bgImageWrapper}
        data-aos="fade-up"
        data-aos-delay="200"
        data-aos-duration="800"
      >
        <img src="/images/blog.png" alt="" />
      </div>

      <div className="container">
        <div className={styles.layout}>
          {/* 🔹 SIDEBAR */}
          <aside className={styles.sidebar} data-aos="fade-right">
            <ul>
              <li
                className={!selectedTag ? styles.active : ''}
                onClick={() => setSelectedTag(null)}
              >
                Wszystkie posty ({blogPosts.length})
              </li>

              <h3>Tagi</h3>

              {visibleSidebarTags.map((tag) => (
                <li
                  key={tag}
                  className={selectedTag === tag ? styles.active : ''}
                  onClick={() => setSelectedTag(tag)}
                >
                  {tag} ({tagCounts[tag]})
                </li>
              ))}

              {allTags.length > SIDEBAR_TAG_LIMIT && (
                <li
                  className={styles.tagsToggle}
                  onClick={() => setShowAllSidebarTags(prev => !prev)}
                >
                  {showAllSidebarTags ? 'Zwiń tagi...' : 'Pokaż więcej tagów...'}
                </li>
              )}
            </ul>
          </aside>

          {/* 🔹 LISTA POSTÓW */}
          <div className={styles.grid}>
            {filteredPosts.map((post, index) => {
              const hasTags = post.tags && post.tags.length > 0;
              const isExpanded = !!expandedTagsPosts[post._id];

              const visibleTags = hasTags
                ? (isExpanded ? post.tags : post.tags.slice(0, CARD_TAG_LIMIT))
                : [];

              return (
                <div
                  key={post._id}
                  className={styles.card}
                  data-aos="fade-up"
                  data-aos-delay={index * 300}
                >
                  {isNewPost(post.date) && (
                    <div className={styles.fakeBadge}>NOWY POST!</div>
                  )}

                  <div className={styles.imageWrapper}>
                    <img
                      src={post.image || '/blog/no-image.png'}
                      alt={post.title || 'Brak tytułu'}
                    />
                  </div>

                  <div className={styles.content}>
                    <div className={styles.meta}>
                      <span>
                        {post.date
                          ? new Date(post.date).toLocaleDateString('pl-PL')
                          : 'Brak daty publikacji'}
                      </span>
                    </div>

                    <h2>{post.title || 'Brak tytułu'}</h2>

                    <p>{post.excerpt || 'Brak opisu tego wpisu.'}</p>

                    {hasTags ? (
                      <>
                        <ul className={styles.tags}>
                          {visibleTags.map((tag, i) => (
                            <li key={i}>
                              <span>🏷️</span> {tag}
                            </li>
                          ))}
                        </ul>

                        {post.tags.length > CARD_TAG_LIMIT && (
                          <button
                            type="button"
                            className={styles.tagsToggleInline}
                            onClick={() =>
                              setExpandedTagsPosts(prev => ({
                                ...prev,
                                [post._id]: !isExpanded,
                              }))
                            }
                          >
                            {isExpanded ? 'Zwiń tagi' : 'Pokaż więcej tagów'}
                          </button>
                        )}
                      </>
                    ) : (
                      <p className={styles.noTags}>Brak tagów</p>
                    )}

                    <div className={styles.separator}></div>

                    {post.slug?.current ? (
                      <Link
                        to={`/blog/${post.slug.current}`}
                        className={styles.button}
                      >
                        Czytaj więcej...
                      </Link>
                    ) : (
                      <span className={styles.buttonDisabled}>
                        Brak linku do posta
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Blog;
